import { useEffect, useState } from "react";
import { useHotelsStyles, useModuleBEM } from "@b2bportal/core-themes";
import {
  CoreHotelsComponent,
  type InputErrorType,
  type IMonthBucket,
} from "@b2bportal/core-types";

import { useI18nContext } from "@hopper-b2b/i18n";
import { useDateRangePicker } from "@tenant/hotels";
import {
  Calendar,
  DatePickerButton,
  Dialog,
  DialogClose,
  DialogContent,
  DialogTrigger,
  PickerType,
} from "@tenant/ui";

import clsx from "clsx";
import { useOptionalDatePickerContext } from "../../overrides/core-hotels/AvailabilityPage/context/DatePickerContext";
import defaultStyles from "./DateRangePicker.module.scss";

export interface DateRangePickerComponentProps {
  startDate: string | undefined;
  endDate: string | undefined;
  onStartDateChangeHandler: (date: string | undefined) => void;
  onEndDateChangeHandler: (date: string | undefined) => void;
  onDoneClick?: () => void;
  onClose?: () => void;
  dialogTitle?: string;
  classNames?: {
    content?: string;
    button?: string;
  };
  pricingProps?: {
    months?: IMonthBucket[];
    priceTagsElement?: JSX.Element;
    priceTags?: string[];
  };
  error?: InputErrorType;
  startDateLabel?: string;
  endDateLabel?: string;
}
export const DateRangePicker = (props: DateRangePickerComponentProps) => {
  const COMPONENT_KEY = CoreHotelsComponent.DateRangePicker;
  const styles = useHotelsStyles(COMPONENT_KEY, defaultStyles);
  const [, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { context, handlers, text } = useDateRangePicker(props);
  const { t } = useI18nContext();
  const [isOpen, setIsOpen] = useState(false);

  // This hook returns undefined if context is not available (e.g., on other pages)
  const datePickerContext = useOptionalDatePickerContext();

  // Register the opener function with the context
  useEffect(() => {
    if (datePickerContext) {
      datePickerContext.setDatePickerOpener(() => setIsOpen(true));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={setIsOpen}
      onClose={handlers.onClose}
      onOpen={handlers.onOpen}
    >
      <DialogTrigger asChild>
        <DatePickerButton
          startDate={context.stringStartDate}
          endDate={context.stringEndDate}
          startLabel={props.startDateLabel ?? text.startDateLabel}
          endLabel={props.endDateLabel ?? text.endDateLabel}
          hideSeparator={true}
          error={props.error}
          className={clsx(cn("date-picker-button"), props.classNames?.button)}
        />
      </DialogTrigger>
      <DialogContent
        title={t("core-hotels.search.dateRangePicker.datesTitle")}
        className={clsx(cn("dialog-content"), props.classNames?.content)}
        fullScreen={context.dialogFullscreen}
        bottomFixedContent={
          <div className={cn("bottom-content")}>
            <DialogClose asChild>
              <button
                className={cn("apply-button")}
                onClick={handlers.onDoneClick}
              >
                {text.dialogDone}
              </button>
            </DialogClose>
          </div>
        }
      >
        <Calendar
          className={clsx(cn("calendar"), props.className)}
          viewType={context.calendarViewType}
          setStartDate={handlers.onStartDateChange}
          setEndDate={handlers.onEndDateChange}
          startDate={context.startDate}
          endDate={context.endDate}
          startDateLabel={props.startDateLabel ?? text.startDateLabel}
          endDateLabel={props.endDateLabel ?? text.endDateLabel}
          pickerType={PickerType.RANGE}
          priceTagsElement={<></>}
          months={props.pricingProps?.months ?? []}
          currency={""}
          priceTags={props.pricingProps?.priceTags ?? []}
        />
      </DialogContent>
    </Dialog>
  );
};
